'use client';

import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { apiLogger } from '../utils/logger';
import { config } from '@/config';
import { attachWindowAttentionListeners } from '@/core/utils/windowAttention';

/**
 * Build the cookie attribute string for CSRF cookies.
 * Includes Domain when config.auth.cookieDomain is set (required for
 * cross-subdomain setups where frontend and API are on different subdomains,
 * e.g. staging.letsvoley.com -> api.staging.letsvoley.com).
 *
 * Canonical implementation — also used by `frontend/src/api/core/csrf.ts`.
 */
export function buildCsrfCookieAttrs(): string {
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const domain = config.auth.cookieDomain;
  const domainAttr = domain ? `Domain=${domain}; ` : '';
  return isSecure
    ? `Path=/; ${domainAttr}SameSite=None; Secure`
    : `Path=/; SameSite=Lax`;
}

interface CsrfContextType {
  csrfToken: string | null;
  isLoading: boolean;
  error: Error | null;
}

const CsrfContext = createContext<CsrfContextType>({
  csrfToken: null,
  isLoading: true,
  error: null,
});

export const useCsrfContext = () => useContext(CsrfContext);

// Global singleton for CSRF token - exported for use in API layer
export let globalCsrfToken: string | null = null;

// Shared fetch promise - exported so baseQueryWithRetry can wait for it
// This prevents duplicate parallel fetches during app initialization
export let csrfInitPromise: Promise<string | null> | null = null;

// Export setter for API layer to update cache
export function setGlobalCsrfToken(token: string | null) {
  globalCsrfToken = token;
}

// Export function to clear the init promise (for testing/reset)
export function clearCsrfInitPromise() {
  csrfInitPromise = null;
}

/**
 * CsrfProvider - Fetches CSRF token once at app initialization
 * This eliminates race conditions during login by ensuring the token is ready.
 *
 * NOTE: CSRF is only required for mutations (POST/PUT/PATCH/DELETE).
 * GET requests (dashboard read-only boot) proceed without waiting for CSRF,
 * so this provider does NOT block initial dashboard data fetching.
 * See baseQueryWithRetry.ts prepareHeaders for exclusion logic.
 */
export function CsrfProvider({ children }: { children: React.ReactNode }) {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    // Only fetch once per app lifecycle
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchToken = async () => {
      try {
        // Return cached token if available
        if (globalCsrfToken) {
          setCsrfToken(globalCsrfToken);
          setIsLoading(false);
          return;
        }

        // Prevent parallel fetches - use the shared promise
        if (csrfInitPromise) {
          const token = await csrfInitPromise;
          setCsrfToken(token);
          setIsLoading(false);
          return;
        }

        // Create fetch promise to prevent duplicates - use the SHARED module-level variable
        // so that baseQueryWithRetry can also wait for this same promise
        csrfInitPromise = (async () => {
          // Check for existing cookie token first - use it immediately if available
          // IMPORTANT: Decode the cookie value - it may be URL-encoded in production
          const rawCookieToken = (document.cookie.match(/(?:^|; )csrf-token=([^;]+)/)?.[1]) || undefined;
          let existingToken: string | undefined;
          if (rawCookieToken) {
            try {
              existingToken = decodeURIComponent(rawCookieToken);
            } catch {
              existingToken = rawCookieToken; // Fallback to raw if decode fails
            }
          }

          if (existingToken) {
            globalCsrfToken = existingToken;
            // Re-set cookie with Domain attribute so it's sent to the API subdomain
            // (old cookies may lack Domain and only scope to the frontend hostname)
            try {
              document.cookie = `csrf-token=${encodeURIComponent(existingToken)}; ${buildCsrfCookieAttrs()}`;
            } catch {}
            apiLogger.info('[CsrfProvider] Using existing cookie token (re-set with Domain)');
            return existingToken;
          }

          // No existing token - fetch from backend with retry
          // Uses same timeout as baseQuery default (15s) to handle cold starts gracefully
          const CSRF_FETCH_TIMEOUT = 15000;
          const MAX_RETRIES = 2; // 3 total attempts (initial + 2 retries)

          // Jittered exponential backoff: base * 2^attempt + random jitter
          const getRetryDelay = (attempt: number): number => {
            const baseDelay = 500; // 500ms base
            const exponentialDelay = baseDelay * Math.pow(2, attempt - 1); // 500ms, 1000ms
            const jitter = Math.random() * 300; // 0-300ms jitter
            return Math.min(exponentialDelay + jitter, 3000); // Cap at 3s
          };

          const fetchWithRetry = async (attempt: number = 1): Promise<string | null> => {
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), CSRF_FETCH_TIMEOUT);

              const response = await fetch(`${config.api.baseUrl}/csrf-token`, {
                method: 'GET',
                credentials: 'include',
                signal: controller.signal,
              }).finally(() => clearTimeout(timeoutId));

              if (!response.ok) {
                throw new Error(`Failed to fetch CSRF token: ${response.status}`);
              }

              const data = await response.json();

              // Handle both response formats (server may send different key names)
              const token = data.csrfToken || data.csrf_token || data.token;
              if (!token) {
                throw new Error('Invalid CSRF token response');
              }

              globalCsrfToken = token;

              // Store token in cookie (single source of truth)
              // IMPORTANT: Must match backend CSRF_SAMESITE setting (none in staging/prod)
              // SameSite=None requires Secure flag
              try {
                document.cookie = `csrf-token=${encodeURIComponent(token)}; ${buildCsrfCookieAttrs()}`;
              } catch {}

              apiLogger.info('[CsrfProvider] CSRF token fetched successfully');
              return token;
            } catch (err) {
              // Retry with jittered backoff (transient error policy)
              if (attempt <= MAX_RETRIES) {
                const delay = getRetryDelay(attempt);
                apiLogger.warn(`[CsrfProvider] CSRF fetch failed (attempt ${attempt}/${MAX_RETRIES + 1}), retrying in ${Math.round(delay)}ms:`, err);
                await new Promise(resolve => setTimeout(resolve, delay));
                return fetchWithRetry(attempt + 1);
              }
              apiLogger.warn('[CsrfProvider] CSRF fetch failed after all retries:', err);
              return null;
            }
          };

          return fetchWithRetry();
        })();

        const token = await csrfInitPromise;
        setCsrfToken(token);
      } catch (err) {
        apiLogger.error('[CsrfProvider] Error initializing CSRF:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
        // NOTE: Do NOT clear csrfInitPromise here - other modules may still be waiting for it
        // The promise resolves with the token value which can be reused
      }
    };

    fetchToken();
  }, []);

  // Proactive CSRF cookie refresh: timer-based + visibility-change-based
  useEffect(() => {
    if (isLoading || !csrfToken) return;

    let isCancelled = false;
    let timerId: NodeJS.Timeout | null = null;

    const CSRF_REFRESH_BASE_MS = 20 * 60 * 1000; // 20 minutes
    const CSRF_JITTER_MS = 2 * 60 * 1000; // ±2 minutes jitter

    const hasCsrfCookie = (): boolean => {
      return /(?:^|; )csrf-token=/.test(document.cookie);
    };

    // In-flight guard to prevent concurrent CSRF refreshes (timer + visibility overlap)
    let refreshInFlight = false;

    const refreshCsrf = async () => {
      if (isCancelled || refreshInFlight) return;
      refreshInFlight = true;
      try {
        const response = await fetch(`${config.api.baseUrl}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok || isCancelled) return;
        const data = await response.json();
        const token = data.csrfToken || data.csrf_token || data.token;
        if (token && !isCancelled) {
          globalCsrfToken = token;
          setCsrfToken(token);
          try {
            document.cookie = `csrf-token=${encodeURIComponent(token)}; ${buildCsrfCookieAttrs()}`;
          } catch {}
          apiLogger.info('[CsrfProvider] Proactive CSRF refresh succeeded');
        }
      } catch (err) {
        apiLogger.warn('[CsrfProvider] Proactive CSRF refresh failed:', err);
      } finally {
        refreshInFlight = false;
      }
    };

    const scheduleRefresh = () => {
      if (isCancelled) return;
      const jitter = (Math.random() * 2 - 1) * CSRF_JITTER_MS; // random in [-jitter, +jitter]
      const interval = CSRF_REFRESH_BASE_MS + jitter;
      timerId = setTimeout(() => {
        refreshCsrf().then(() => {
          if (!isCancelled) scheduleRefresh();
        });
      }, interval);
    };

    scheduleRefresh();

    const detachAttentionListeners = attachWindowAttentionListeners(() => {
      if (!hasCsrfCookie()) {
        apiLogger.info('[CsrfProvider] Tab visible + CSRF cookie missing, refreshing');
        void refreshCsrf();
      }
    }, {
      onVisible: true,
      onFocus: false,
      onOnline: false,
      minIntervalMs: 250,
    });

    return () => {
      isCancelled = true;
      if (timerId) clearTimeout(timerId);
      detachAttentionListeners();
    };
  }, [isLoading, csrfToken]);

  // Memoize context value to prevent cascading re-renders through the
  // provider tree when CSRF token refreshes proactively (~every 20 min).
  const value = useMemo(
    () => ({ csrfToken, isLoading, error }),
    [csrfToken, isLoading, error],
  );

  return (
    <CsrfContext.Provider value={value}>
      {children}
    </CsrfContext.Provider>
  );
}

/**
 * Get the current CSRF token synchronously.
 * Checks the cookie first (single source of truth), then falls back to the
 * in-memory global. Updates the global when the cookie contains a newer value.
 *
 * Canonical implementation — `getGlobalCsrfToken()` delegates here.
 */
export function getCachedCsrfToken(): string | null {
  if (typeof window !== 'undefined') {
    const rawCookieToken = (document.cookie.match(/(?:^|; )csrf-token=([^;]+)/)?.[1]) || undefined;

    if (rawCookieToken) {
      // IMPORTANT: Decode URL-encoded cookie value (cookie is set with encodeURIComponent)
      try {
        const decodedToken = decodeURIComponent(rawCookieToken);
        globalCsrfToken = decodedToken;
        return decodedToken;
      } catch {
        // Fallback to raw value if decode fails
        globalCsrfToken = rawCookieToken;
        return rawCookieToken;
      }
    }
  }

  return globalCsrfToken;
}

/**
 * Async wrapper around getCachedCsrfToken() for backward compatibility.
 * The logic is identical (purely synchronous) — the async signature is
 * retained to avoid breaking existing callers that `await` it.
 */
export async function getGlobalCsrfToken(): Promise<string | null> {
  return getCachedCsrfToken();
}